import { formatCurrency, formatDate } from "../formatters";
const CHIP_FORMATTERS = {
    currency: (value)=>formatCurrency(+value),
    date: (value)=>formatDate(value)
};
const isRangeDef = (def)=>def.type === 'dateRange' || def.type === 'numberRange';
/** resolve a def's chip value formatter from its `format` (dates default to 'date') */ const chipFormatter = (def)=>{
    if ('format' in def && def.format) {
        return CHIP_FORMATTERS[def.format];
    }
    if (def.type === 'dateRange') {
        return CHIP_FORMATTERS.date;
    }
    return (value)=>value;
};
/**
 * Build query params from a filter-def schema. Ranges emit two params
 * (`startKey`/`endKey`), dates anchor to UTC, multiSelect joins to CSV.
 */ export const toFilterParams = (defs, values)=>{
    const params = {};
    for (const def of defs){
        if (isRangeDef(def)) {
            const start = values[def.startName];
            const end = values[def.endName];
            if (!isEmpty(start)) {
                params[def.startKey ?? def.startName] = def.type === 'dateRange' ? toUtcStartOfDay(start) : Number(start);
            }
            if (!isEmpty(end)) {
                params[def.endKey ?? def.endName] = def.type === 'dateRange' ? toUtcEndOfDay(end) : Number(end);
            }
            continue;
        }
        const value = values[def.name];
        if (isEmpty(value)) {
            continue;
        }
        const key = def.apiKey ?? def.name;
        switch(def.type){
            case 'number':
                params[key] = Number(value);
                break;
            case 'multiSelect':
                params[key] = value.join(',');
                break;
            case 'switch':
                if (value === true || value === 'true') {
                    params[key] = 'true';
                }
                break;
            default:
                params[key] = String(value);
                break;
        }
    }
    return params;
};
/**
 * Build active-filter chips from the same schema. One chip per range (its ✕
 * clears both bounds), per-item chips for multiSelect. `applyPatch` merges a
 * partial set of fields in one commit, so the caller stays the single owner of
 * filter state and a range chip clears both bounds atomically.
 * `collapseChipsAfter` defaults to 3: a multiSelect holding more than that many
 * values renders one summary chip whose ✕ clears the whole filter, since
 * per-value ✕ stays available on the popover's own chips. Pass a larger number
 * (or `Infinity`) to keep every value as its own chip.
 */ const DEFAULT_COLLAPSE_CHIPS_AFTER = 3;
export const toFilterChips = (defs, values, applyPatch, collapseChipsAfter = DEFAULT_COLLAPSE_CHIPS_AFTER)=>{
    const chips = [];
    for (const def of defs){
        if (isRangeDef(def)) {
            const start = values[def.startName];
            const end = values[def.endName];
            if (isEmpty(start) && isEmpty(end)) {
                continue;
            }
            const fmt = chipFormatter(def);
            const from = isEmpty(start) ? '…' : fmt(String(start));
            const to = isEmpty(end) ? '…' : fmt(String(end));
            chips.push({
                key: `${def.startName}-${def.endName}`,
                label: `${def.label}: ${from} – ${to}`,
                onRemove: ()=>applyPatch({
                        [def.endName]: '',
                        [def.startName]: ''
                    })
            });
            continue;
        }
        const value = values[def.name];
        if (isEmpty(value)) {
            continue;
        }
        if (def.type === 'multiSelect') {
            const codes = value;
            if (codes.length > collapseChipsAfter) {
                chips.push({
                    key: def.name,
                    label: `${def.label}: ${codes.length} selected`,
                    onRemove: ()=>applyPatch({
                            [def.name]: []
                        })
                });
                continue;
            }
            for (const code of codes){
                const optionLabel = def.options.find((option)=>option.value === code)?.label ?? code;
                chips.push({
                    key: `${def.name}-${code}`,
                    label: `${def.label}: ${optionLabel}`,
                    onRemove: ()=>applyPatch({
                            [def.name]: codes.filter((current)=>current !== code)
                        })
                });
            }
            continue;
        }
        if (def.type === 'switch') {
            if (value === true || value === 'true') {
                chips.push({
                    key: def.name,
                    label: def.label,
                    onRemove: ()=>applyPatch({
                            [def.name]: ''
                        })
                });
            }
            continue;
        }
        const display = def.type === 'select' || def.type === 'autocomplete' ? def.options.find((option)=>option.value === value)?.label ?? String(value) : chipFormatter(def)(String(value));
        chips.push({
            key: def.name,
            label: `${def.label}: ${display}`,
            onRemove: ()=>applyPatch({
                    [def.name]: ''
                })
        });
    }
    return chips;
};
const isEmpty = (value)=>value == null || value === '' || Array.isArray(value) && value.length === 0;
/** the row field a def filters on: `column` when it mirrors a differently named column, else its own name */ export const filterFieldOf = (def)=>isRangeDef(def) ? def.column ?? def.startName : def.column ?? def.name;
const SCALAR_MATCHERS = {
    autocomplete: (rowValue, value)=>String(rowValue) === String(value),
    dateRange: ()=>true,
    multiSelect: (rowValue, value)=>value.includes(String(rowValue)),
    number: (rowValue, value)=>+String(rowValue) === +String(value),
    numberRange: ()=>true,
    select: (rowValue, value)=>String(rowValue) === String(value),
    switch: (rowValue, value)=>value === true || value === 'true' ? !!rowValue : true,
    text: (rowValue, value)=>String(rowValue ?? '').toLowerCase().includes(String(value).toLowerCase())
};
/** client-side counterpart of `toFilterParams`: true when `row` satisfies every populated filter (ranges are inclusive, dates compare on their first 10 characters) */ export const matchesFilters = (defs, values, row)=>defs.every((def)=>{
        const rowValue = row[filterFieldOf(def)];
        if (isRangeDef(def)) {
            const start = values[def.startName];
            const end = values[def.endName];
            const isNumber = def.type === 'numberRange';
            const actual = isNumber ? +String(rowValue) : String(rowValue).slice(0, 10);
            const min = isNumber ? +String(start) : String(start).slice(0, 10);
            const max = isNumber ? +String(end) : String(end).slice(0, 10);
            if (!isEmpty(start) && (rowValue == null || actual < min)) return false;
            if (!isEmpty(end) && (rowValue == null || actual > max)) return false;
            return true;
        }
        const value = values[def.name];
        return isEmpty(value) || SCALAR_MATCHERS[def.type](rowValue, value);
    });
/** day-anchored to UTC so ranges match the server's UTC dates regardless of viewer timezone */ const toUtcStartOfDay = (value)=>`${String(value).slice(0, 10)}T00:00:00.000Z`;
const toUtcEndOfDay = (value)=>`${String(value).slice(0, 10)}T23:59:59.999Z`;
/**
 * Build API query params from filter form values, driven by a field schema.
 * Handles empties (including numbers, whose `.length` is undefined), the
 * per-type transform, the array→CSV join, and the form-name → API-key remap.
 */ export const buildFilterParams = (fields, values)=>{
    const params = {};
    for (const field of fields){
        const value = values[field.name];
        if (isEmpty(value)) {
            continue;
        }
        const key = field.apiKey ?? field.name;
        switch(field.type){
            case 'number':
                params[key] = Number(value);
                break;
            case 'multiSelect':
                params[key] = value.join(',');
                break;
            case 'dateStart':
                params[key] = toUtcStartOfDay(value);
                break;
            case 'dateEnd':
                params[key] = toUtcEndOfDay(value);
                break;
            default:
                params[key] = String(value);
                break;
        }
    }
    return params;
};
const chipValue = (field, value)=>{
    if (field.formatChipValue) {
        return field.formatChipValue(String(value));
    }
    if (field.type === 'select') {
        return field.options?.find((option)=>option.value === value)?.label ?? String(value);
    }
    if (field.type === 'dateStart' || field.type === 'dateEnd') {
        return formatDate(String(value));
    }
    return String(value);
};
/**
 * Build the active-filter chips from the same field schema. `setValue` sets a
 * field to a new value (`''` to clear a scalar, the filtered array to drop one
 * multiSelect item) so the caller stays the single owner of filter state.
 */ export const buildFilterChips = (fields, values, setValue)=>{
    const chips = [];
    for (const field of fields){
        const value = values[field.name];
        if (isEmpty(value)) {
            continue;
        }
        if (field.type === 'multiSelect') {
            const codes = value;
            for (const code of codes){
                const label = field.options?.find((option)=>option.value === code)?.label ?? code;
                chips.push({
                    key: `${field.name}-${code}`,
                    label: `${field.label}: ${label}`,
                    onRemove: ()=>setValue(field.name, codes.filter((current)=>current !== code))
                });
            }
            continue;
        }
        chips.push({
            key: field.name,
            label: `${field.label}: ${chipValue(field, value)}`,
            onRemove: ()=>setValue(field.name, '')
        });
    }
    return chips;
};

//# sourceMappingURL=filterFields.js.map